import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useApi } from '../../hooks/useApi';

const isNumeric = (v) =>
    v !== "" && !isNaN(Number(String(v).replace(",", ".")));

const isExcelSerialNumber = (v) => typeof v === "number" && Number.isFinite(v);

// Rango plausible de fechas de Excel (1970-01-01 a ~ 2100-12-31)
const isPlausibleExcelSerialDate = (v) => isExcelSerialNumber(v) && v >= 25569 && v <= 85000;

const isDDMMYYYY = (s) => {
    if (typeof s !== "string") return false;
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return false;
    const d = Number(m[1]), mo = Number(m[2]) - 1, y = Number(m[3]);
    const dt = new Date(y, mo, d);
    return dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d;
};

const isDateValue = (v) => {
    if (isPlausibleExcelSerialDate(v)) return true;
    if (isDDMMYYYY(String(v))) return true;
    return false;
};



const normalizeDate = (v) => {
    let dt = null;
    if (isPlausibleExcelSerialDate(v)) {
        const d = XLSX.SSF.parse_date_code(v);
        if (d) dt = new Date(Date.UTC(d.y, d.m - 1, d.d));
    } else if (isDDMMYYYY(String(v))) {
        const [dd, mm, yyyy] = String(v).split("/");
        dt = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        dt = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    }
    if (!dt || isNaN(dt.getTime())) return "";
    const day = String(dt.getUTCDate()).padStart(2, "0");
    const month = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const year = dt.getUTCFullYear();
    return `${day}/${month}/${year}`; // dd/mm/aaaa
};

const typeValidators = {
    int: (v) => Number.isInteger(Number(String(v).trim())),
    decimal: (v) => !isNaN(parseFloat(String(v).replace(",", "."))),
    bool: (v) => ["true", "false", "1", "0", "sí", "si", "no"].includes(String(v).toLowerCase()),
    date: (v) => isDateValue(v),
    string: (_) => true,
};

const validateColumnSample = (
    matrix,
    layout,
    startRow,
    sampleCount = 10,
    threshold = 0.7
) => {
    const end = Math.min(matrix.length, startRow + sampleCount);

    for (let ci = 0; ci < layout.length; ci++) {
        const colDef = layout[ci]; // <- aquí, no "def"
        const type = (
            colDef?.type ||
            colDef?.columnType ||
            colDef?.dataType ||
            colDef?.DataType ||
            "string"
        ).toLowerCase();

        let nonEmpty = 0,
            valid = 0;

        for (let r = startRow; r < end; r++) {
            const raw = (matrix[r] ?? [])[ci];
            const valStr = String(raw ?? "").trim();
            if (valStr === "") continue; // solo cuenta valores no vacíos
            nonEmpty++;
            const ok = (typeValidators[type] || typeValidators.string)(raw);
            if (ok) valid++;
        }

        if (nonEmpty > 0) {
            const ratio = valid / nonEmpty;
            if (ratio < threshold) {
                const label =
                    colDef?.label ||
                    colDef?.columnName ||
                    colDef?.DisplayName ||
                    `Col ${ci + 1}`;
                return { ok: false, columnIndex: ci, label, ratio };
            }
        }
    }
    return { ok: true };
};

const detectHasHeader = (matrix) => {
    if (!matrix.length) return false;
    const first = matrix[0]?.[0];
    // Si NO es fecha dd/mm/aaaa NI número, asumimos que la primera fila es encabezado
    const looksLikeHeader = !(isDDMMYYYY(String(first)) || isNumeric(first));
    return looksLikeHeader;
};

// normaliza texto para comparaciones sueltas
const norm = (s) =>
    String(s ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

// ¿cumple tipo?
const isValidByType = (raw, type) => {
    const t = (type || "string").toLowerCase();
    return (typeValidators[t] || typeValidators.string)(raw);
};

// puntaje de “parece fila de datos” según layout
const scoreRow = (row, layout) => {
    const expected = layout.length;
    if (!row || row.length < expected) return -999; // penaliza ancho incorrecto

    let score = 0;
    for (let i = 0; i < expected; i++) {
        const col = layout[i];
        const raw = row[i] ?? "";
        const valStr = String(raw).trim();
        const required = !!col.required || !!col.IsRequired || !!col.isRequired;
        const type = (col?.type || col?.columnType || col?.dataType || col?.DataType || "string").toLowerCase();

        if (valStr === "") {
            if (required) score -= 1;     // requerido vacío: resta
            else score += 0.25;           // opcional vacío: leve
        } else {
            score += isValidByType(raw, type) ? 1 : -1; // válido suma, inválido resta
        }
    }
    return score;
};

// decide si empezamos en 0 (sin encabezado) o 1 (con encabezado)
const pickDataStartIndex = (matrix, layout) => {
    const r0 = matrix[0] || [];
    const r1 = matrix[1] || [];
    const s0 = scoreRow(r0, layout);
    const s1 = scoreRow(r1, layout);

    // Si la segunda fila puntúa claramente mejor, asumimos encabezado en la 1ª
    if (s1 > s0 + 1) return { start: 1, hasHeader: true };
    return { start: 0, hasHeader: false };
};

/**
 * ExcelUploader
 * Props:
 * - clientName: string (obligatorio para pedir el layout)
 * - onDataLoaded?: ({ layout, rows, errors, hasHeader }) => void
 */
const ExcelUploader = ({ clientName, onDataLoaded, showPreview = true, previewOnly = false, externalData = null }) => {
    const [layout, setLayout] = useState([]); // [{ColumnOrder, DisplayName, DataType, IsRequired, MaxLength, Regex}]
    const [fileName, setFileName] = useState(null);
    const [rows, setRows] = useState([]); // datos normalizados
    const [errors, setErrors] = useState([]); // [{rowIndex, colIndex, message}]
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const { get, loading: apiLoading } = useApi();
    const [fileError, setFileError] = useState("");
    const isCellEmpty = (v) => String(v ?? "").trim() === "";
    const isMatrixRowEmpty = (row) => !row || row.every(isCellEmpty);

    // 1) Cargar layout del cliente
    useEffect(() => {
        if (!clientName) return;

        const fetchLayout = async () => {
            setFetchError("");
            setLayout([]);

            try {
                const data = await get("layoutByClient", {
                    urlParams: { id: clientName },
                    showError: false,
                    errorMessage: "Layout no encontrado",
                });
                console.log(data);
                setLayout(data || []);
            } catch (err) {
                const msg =
                    err?.response?.data?.message || err.message || "Error al cargar layout";
                setFetchError(msg);
            }
        };

        fetchLayout();
    }, [clientName, get]);

    const columns = useMemo(() =>
        layout.map((c, i) => {
            const display = (c?.ColumnKey && String(c.ColumnKey).trim()) || "";
            const colName = (c?.columnName && String(c.columnName).trim()) || "";
            const key = display || colName || `col_${i + 1}`;     // clave interna para el row
            const label = display || colName || `Col ${i + 1}`;   // texto visible en el header
            return { key, label, def: c, index: i };
        }),
        [layout]
    );

    // 2) Leer archivo y validar contra el layout por índice de columna
    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const localFileName = file.name;
        setFileName(localFileName);
        setErrors([]);
        setRows([]);
        setFileError(""); 

        if (layout.length === 0) {
            setFetchError("No hay layout para este cliente.");
            return;
        }

        const allowedExt = ["xls", "xlsx", "csv"];
        const allowedMime = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "text/csv",
            "text/plain" // a veces csv viene como text/plain
        ];

        const ext = (localFileName.split(".").pop() || "").toLowerCase();
        const mime = (file.type || "").toLowerCase();

        if (!allowedExt.includes(ext) || (mime && !allowedMime.includes(mime) && ext !== "csv")) {
            // nota: algunos .xls/.xlsx pueden venir con mime vacío en ciertos browsers, por eso priorizamos extensión
            setFileError(`Tipo de archivo no permitido. Solo se aceptan: ${allowedExt.join(", ")}.`);
            // opcional: limpiar el input para forzar re-selección
            e.target.value = "";
            return;
        }


        const reader = new FileReader();
        reader.onload = (evt) => {
            const wb = XLSX.read(evt.target.result, { type: "binary" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

            if (matrix.length === 0) {
                setErrors([{ rowIndex: 0, colIndex: 0, message: "El archivo está vacío" }]);
                return;
            }

            const expectedCols = layout.length;

            // 1) Detectar automáticamente la primera fila de datos
            const { start, hasHeader } = pickDataStartIndex(matrix, layout);

            // 2) Validar ancho y “compatibilidad general” en un muestreo inicial
            const sampleRows = matrix.slice(start, start + 10); // primeras 10 filas de datos
            const widthMismatch = sampleRows.some((r) => (r?.length ?? 0) < expectedCols);
            if (widthMismatch) {
                setErrors([{
                    rowIndex: start + 1,
                    colIndex: 1,
                    message: `El archivo no tiene las ${expectedCols} columnas esperadas.`,
                }]);
                return;
            }

            const colSample = validateColumnSample(matrix, layout, start, 10, 0.7); // 70% de acierto mínimo
            if (!colSample.ok) {
                setErrors([{
                    rowIndex: start + 1,
                    colIndex: colSample.columnIndex + 1,
                    message: `La columna "${colSample.label}" no coincide con el tipo esperado (compatibilidad ${Math.round(colSample.ratio * 100)}% en la muestra).`,
                }]);
                return;
            }

            // Si el archivo es de otra cadena, los tipos fallarán mucho.
            // Umbral: si el promedio de score es muy bajo, aborta.
            const avgScore =
                sampleRows.reduce((acc, r) => acc + scoreRow(r, layout), 0) /
                Math.max(sampleRows.length, 1);

            if (avgScore < 0) {
                setErrors([{
                    rowIndex: start + 1,
                    colIndex: 1,
                    message:
                        "El archivo no corresponde al layout esperado (tipos/orden de columnas no coinciden).",
                }]);
                return;
            }

            // 3) Procesar filas reales
            let lastIdx = -1;
            for (let i = matrix.length - 1; i >= start; i--) {
                if (!isMatrixRowEmpty(matrix[i])) { lastIdx = i; break; }
            }
            if (lastIdx === -1) {
                setErrors([{ rowIndex: 1, colIndex: 1, message: "No se detectaron datos" }]);
                return;
            }
            const dataRowsRaw = matrix.slice(start, lastIdx + 1);

            // 2) filtra filas totalmente vacías intermedias
            const dataRows = dataRowsRaw.filter(r => !isMatrixRowEmpty(r));

            const newErrors = [];
            const normalized = dataRows.map((r, ri) => {
                const obj = {};
                let rowHasAnyValue = false;
                layout.forEach((colDef, ci) => {
                    const key = (colDef.ColumnKey || colDef.columnKey || `col_${ci}`).trim(); // 👈 clave INTERNA estable
                    const friendly = colDef.DisplayName || colDef.displayName || key;          // 👈 para mensajes
                    //const type = (colDef.DataType || colDef.dataType || "string").toLowerCase();
                    const type = (
                           colDef?.type || colDef?.columnType || colDef?.ColumnType || colDef?.dataType || colDef?.DataType || "string"
                         ).toLowerCase();
                    const required = !!(colDef.IsRequired ?? colDef.isRequired);
                    const raw = r[ci] ?? "";
                    const valStr = String(raw).trim();

                    // si hay cualquier valor no vacío en la fila, marcamos bandera
                    if (valStr !== "") rowHasAnyValue = true;

                    // requerido
                    if (required && valStr === "") {
                        newErrors.push({
                            rowIndex: (hasHeader ? 2 : 1) + ri,
                            colIndex: ci + 1,
                            message: `Campo requerido vacío (${friendly}).`,
                        });
                    } else if (valStr !== "") {
                        const isValid = (typeValidators[type] || typeValidators.string)(raw);
                        if (!isValid) {
                            newErrors.push({
                                rowIndex: (hasHeader ? 2 : 1) + ri,
                                colIndex: ci + 1,
                                message: `Valor inválido para ${friendly} (${type}).`,
                            });
                        }
                        if (colDef.MaxLength && type === "string" && valStr.length > colDef.MaxLength) {
                            newErrors.push({
                                rowIndex: (hasHeader ? 2 : 1) + ri,
                                colIndex: ci + 1,
                                message: `Longitud > ${colDef.MaxLength} en ${friendly}.`,
                            });
                        }
                        if (colDef.Regex) {
                            try {
                                const re = new RegExp(colDef.Regex);
                                if (!re.test(valStr)) {
                                    newErrors.push({
                                        rowIndex: (hasHeader ? 2 : 1) + ri,
                                        colIndex: ci + 1,
                                        message: `Formato inválido en ${friendly}.`,
                                    });
                                }
                            } catch { }
                        }
                    }

                    // normalización por tipo
                    let normalizedValue = valStr;

                    // helper: intenta parsear números prettily (paréntesis, comas, puntos)
                    const parseNumericPretty = (v) => {
                        if (v === "" || v == null) return NaN;
                        let s = String(v).trim();
                        // si viene entre paréntesis: (1.23) -> negativo
                        let neg = false;
                        if (/^\(.*\)$/.test(s)) {
                            neg = true;
                            s = s.replace(/^\(|\)$/g, "");
                        }
                        // quitar espacios y miles (espacios o comas como separador de miles)
                        s = s.replace(/\s+/g, "");
                        // si tiene coma y punto: asumir separador de miles y decimal según posición
                        // simplificación: sustituimos coma por punto para decimales comunes
                        s = s.replace(/,/g, ".");
                        // quitar cualquier caracter no numérico salvo punto y signo menos
                        s = s.replace(/[^0-9.\-]/g, "");
                        let n = Number(s);
                        if (isNaN(n)) return NaN;
                        if (neg) n = -n;
                        return n;
                    };

                    if (type === "date") {
                        normalizedValue = normalizeDate(raw);             // dd/mm/aaaa → dd/mm/aaaa normalizado
                    } else if (type === "decimal") {
                        if (valStr === "") normalizedValue = "";
                        else {
                            const n = parseNumericPretty(raw);
                            if (!isNaN(n)) {
                                // convertimos a POSITIVO
                                const abs = Math.abs(n);
                                // mantener como string con punto decimal (igual que antes)
                                normalizedValue = String(abs);
                            } else {
                                // fallback al parseo simple (como tenías)
                                normalizedValue = String(parseFloat(valStr.replace(",", ".")));
                            }
                        }
                    } else if (type === "int") {
                        if (valStr === "") normalizedValue = "";
                        else {
                            const n = parseNumericPretty(raw);
                            if (!isNaN(n)) {
                                const absInt = Math.abs(Math.round(n));
                                normalizedValue = String(absInt);
                            } else {
                                normalizedValue = String(parseInt(valStr, 10));
                            }
                        }
                    } else if (type === "bool") {
                        const l = valStr.toLowerCase();
                        if (["true", "1", "sí", "si"].includes(l)) normalizedValue = "true";
                        else if (["false", "0", "no"].includes(l)) normalizedValue = "false";
                    }

                    // 👇 GUARDA con ColumnKey (clave interna)
                    obj[key] = normalizedValue;
                });
                return rowHasAnyValue ? obj : null;
            }).filter(Boolean); // quita nulls

            setErrors(newErrors);
            setRows(normalized);
            //console.log("[ExcelUploader] onDataLoaded -> fileName:", localFileName, "rows:", normalized?.length, "errors:", newErrors?.length, "hasHeader:", hasHeader);
            onDataLoaded && onDataLoaded({ layout, rows: normalized, errors: newErrors, hasHeader, fileName: localFileName, });
        };
        reader.readAsBinaryString(file);
    };

    if (previewOnly && externalData) {
        const extRows = Array.isArray(externalData.rows) ? externalData.rows : [];
        const extLayout = Array.isArray(externalData.layout) ? externalData.layout : layout; // fallback
        const extFileName = externalData.fileName || "";

        return (
            <div className="p-4 border rounded-md shadow-md bg-white">
                <div className="mb-3 flex flex-col gap-1">
                    <div className="text-sm text-slate-600">
                        Cliente: <b>{clientName || "(sin seleccionar)"}</b>
                    </div>
                    <div className="text-sm text-slate-500">Archivo: <b>{extFileName || "(sin nombre)"}</b></div>
                </div>

                {(!extRows || extRows.length === 0) && (
                    <div className="text-sm text-gray-500">No hay filas para mostrar</div>
                )}

                {showPreview && extRows.length > 0 && (
                    <div className="overflow-x-auto max-h-96 border mt-4">
                        <table className="min-w-full text-sm text-left border">
                            <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                    {(extLayout && extLayout.length > 0)
                                        ? extLayout.map((c, i) => (
                                            <th key={i} className="px-3 py-2 border">
                                                {c.DisplayName || c.DisplayName || c.ColumnKey || c.columnName || `Col ${i + 1}`}
                                            </th>
                                        ))
                                        : (Object.keys(extRows[0] || {}).map((k) => (
                                            <th key={k} className="px-3 py-2 border">{k}</th>
                                        )))
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                {extRows.map((row, idx) => (
                                    <tr key={idx}>
                                        {(extLayout && extLayout.length > 0)
                                            ? extLayout.map((colDef, ci) => {
                                                const key = (colDef.ColumnKey || colDef.columnKey || `col_${ci}`).trim();

                                                return (
                                                    <td key={key} className="px-3 py-2 border">
                                                        {(() => {
                                                            const v = row[key];
                                                            if (v == null || v === "") return "-";
                                                            const label = colDef.columnName;
                                                            if (label === "Total" || label === "Precio") {
                                                                const n = Number(v);
                                                                return isNaN(n) ? v : n.toFixed(2);
                                                            }

                                                            return v;
                                                        })()}
                                                    </td>
                                                );
                                            })
                                            : Object.keys(row).map((k) => (
                                                <td key={k} className="px-3 py-2 border">{row[k]}</td>
                                            ))
                                        }
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    // ----> falta el return normal aquí: input + showPreview table
    // Si no estamos en previewOnly, mostramos el uploader normal:
    return (
        <div className="p-4 border rounded-md shadow-md bg-white">
            <div className="mb-3 flex flex-col gap-1">
                <div className="text-sm text-slate-600">
                    Cliente: <b>{clientName || "(sin seleccionar)"}</b>
                </div>
                {loading && (
                    <div className="text-sm text-slate-500">Cargando layout…</div>
                )}
                {fetchError && (
                    <div className="text-sm text-red-600">Layout: {fetchError}</div>
                )}
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecciona un archivo (xls, xlsx, csv):
            </label>
            <input
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileUpload}
                className="mb-2"
                disabled={!layout.length}
            />

            {fileName && (
                <p className="text-sm text-gray-600">Archivo: {fileName}</p>
            )}

            {fileError && (
                <div className="text-sm text-red-600 mb-2">{fileError}</div>
            )}

            {!!errors.length && (
                <div className="mt-3 text-sm text-red-600">
                    <b>Errores de validación:</b>
                    <ul className="list-disc ml-5">
                        {errors.slice(0, 50).map((e, i) => (
                            <li key={i}>
                                {e.message}
                            </li>
                        ))}
                    </ul>
                    {errors.length > 50 && (
                        <div>…y {errors.length - 50} más</div>
                    )}
                </div>
            )}

            {rows.length > 0 && showPreview && (
                <div className="overflow-x-auto max-h-96 border mt-4">
                    <table className="min-w-full text-sm text-left border">
                        <thead className="bg-gray-100 sticky top-0">
                            <tr>
                                {columns.map(col => (
                                    <th key={col.key} className="px-3 py-2 border">{col.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr key={idx}>
                                    {layout.map((col, ci) => {
                                        const key = col.ColumnKey || col.columnKey || `col_${ci}`;
                                        return (
                                            <td key={key} className="px-3 py-2 border">
                                                {row[key]}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ExcelUploader;
